import { useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import API from '../utils/api';

/**
 * Global pending-shares watcher: polls the pending invites endpoint every 30s,
 * fires ONE bulk toast for any newly-arrived invites, and publishes the current
 * list to the rest of the app via a `pending-shares-updated` window event so
 * the Calendar page can render it without running its own poll.
 *
 * The "Review" action on the toast dispatches `navigate-calendar-invites` —
 * Dashboard handles that to switch to the Calendar page and open the
 * "Pending Invites" tab so the receiver lands exactly where they need to act.
 */
export function usePendingSharesNotifier(enabled) {
  var firstLoadRef = useRef(true);
  var seenIdsRef = useRef(new Set());

  useEffect(function () {
    if (!enabled) return undefined;
    var cancelled = false;

    var fetchOnce = async function (opts) {
      try {
        var r = await axios.get(API + '/calendar/shared-events/pending', { withCredentials: true });
        if (cancelled) return;
        var list = r.data || [];

        // Publish current list so CalendarPage renders without polling itself.
        window.dispatchEvent(new CustomEvent('pending-shares-updated', { detail: list }));

        // On every poll after the first, detect NEW invite ids and fire a single bulk toast.
        if (!firstLoadRef.current) {
          var newOnes = list.filter(function (ps) { return !seenIdsRef.current.has(ps.id); });
          if (newOnes.length > 0) {
            var fromSet = {};
            newOnes.forEach(function (ps) {
              var f = ps.from_user_name || ps.from_user_email || 'Co-parent';
              fromSet[f] = (fromSet[f] || 0) + 1;
            });
            var fromNames = Object.keys(fromSet);
            var header = newOnes.length === 1
              ? 'New event invite from ' + fromNames[0]
              : newOnes.length + ' new event invites' + (fromNames.length === 1 ? ' from ' + fromNames[0] : '');
            var firstTitles = newOnes.slice(0, 3).map(function (ps) {
              return (ps.event_snapshot && ps.event_snapshot.title) || 'Untitled event';
            }).join(', ');
            var descSuffix = newOnes.length > 3 ? ' + ' + (newOnes.length - 3) + ' more' : '';
            toast.info(header, {
              description: firstTitles + descSuffix,
              duration: 10000,
              action: {
                label: 'Review',
                onClick: function () {
                  window.dispatchEvent(new CustomEvent('navigate-calendar-invites'));
                },
              },
            });
          }
        }

        // First-load announcement (if any pending invites already exist on entry).
        if (opts && opts.announceInitial && firstLoadRef.current && list.length > 0) {
          toast.info(
            (list.length === 1 ? '1 pending event invite' : list.length + ' pending event invites') +
            ' from your co-parent',
            {
              duration: 6000,
              action: {
                label: 'Review',
                onClick: function () {
                  window.dispatchEvent(new CustomEvent('navigate-calendar-invites'));
                },
              },
            }
          );
        }

        var ids = new Set();
        list.forEach(function (ps) { ids.add(ps.id); });
        seenIdsRef.current = ids;
        firstLoadRef.current = false;
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('pending-shares poll failed', e);
      }
    };

    fetchOnce({ announceInitial: true });
    var id = setInterval(function () { fetchOnce(); }, 30000);

    // Refetch when tab regains focus so a returning user sees the latest state quickly.
    var onFocus = function () { fetchOnce(); };
    window.addEventListener('focus', onFocus);

    // Allow any component (e.g., CalendarPage after accept/decline) to force a refresh.
    var onRefresh = function () { fetchOnce(); };
    window.addEventListener('pending-shares-refresh', onRefresh);

    return function () {
      cancelled = true;
      clearInterval(id);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('pending-shares-refresh', onRefresh);
    };
  }, [enabled]);
}
